import { useState } from "react";
import type { NativeScrollEvent } from "react-native";
import { Plus as PlusIcon } from "@tamagui/lucide-icons";
import { Button, HorizontalSelectBar, LoadingButton, LoadMoreIndicator, ScrollView, Title5, useMedia, validToken, XStack, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { useBoolVariation } from "../../../lib/flag";
import { CASES_LIST_NEW_CASE_BUTTON_SOURCE, CASES_PANEL_NEW_CASE_BUTTON_SOURCE, usePushGetHelp } from "../../../lib/usePushGetHelp";
import type { CaseListFilters, Issue } from "../utils/types";
import { filterOptions } from "../utils/utils";
import { CaseListFilterPopover } from "./CaseListFilterPopover";
import { CaseListInner } from "./CaseListInner";
import { CaseListItemLegacy } from "./CaseListItem";
import { EmptyCaseListLegacy } from "./EmptyCaseList";
type CaseListLegacyProps = {
  issues: Issue[];
  fetchMore: () => Promise<void>;
  onRefresh: () => Promise<void>;
  loading: boolean;
  selectedFilter: string;
  onTabPress: (id: string) => void;
  endReached: boolean;
  currentIssueId?: string;
};
type CaseListV2Props = CaseListFilters & {
  issues: Issue[];
  fetchMore: () => Promise<void>;
  loading: boolean;
  onFilterChange: (filters: CaseListFilters) => void;
  endReached: boolean;
  currentIssueId?: string;
  markKeepOpen?: () => void;
  onRefresh?: () => Promise<void>;
  source: string;
};
export type CaseListProps = CaseListV2Props | CaseListLegacyProps;
const isCaseListV2Props = (props: CaseListProps): props is CaseListV2Props => {
  return "source" in props;
};
export const CaseList = (props: CaseListProps) => {
  const showCasesPanelV2 = useBoolVariation(Flag.NEW_CASELIST_UI, false);
  if (showCasesPanelV2 && isCaseListV2Props(props)) {
    return <CaseListV2 {...props} />;
  }
  if (!isCaseListV2Props(props)) {
    return <CaseListLegacy {...props} />;
  }
  return null;
};
const isCloseToBottom = ({
  layoutMeasurement,
  contentOffset,
  contentSize
}: NativeScrollEvent) => {
  const verticalOffsetMargin = 60;
  return layoutMeasurement.height + contentOffset.y >= contentSize.height - verticalOffsetMargin;
};

/** Flag OFF: exact original CaseList UI */
const CaseListLegacy = ({
  issues,
  onTabPress,
  loading,
  selectedFilter,
  fetchMore,
  currentIssueId,
  endReached
}: CaseListLegacyProps) => {
  const [fetchingMore, setFetchingMore] = useState(false);
  const isEmpty = issues.length === 0;
  const media = useMedia();
  const {
    pushGetHelp
  } = usePushGetHelp();
  const handleFetchMore = async () => {
    if (!fetchingMore) {
      setFetchingMore(true);
      await fetchMore();
      setFetchingMore(false);
    }
  };
  const handleNewCasePress = () => {
    pushGetHelp({
      caseType: "OTHER",
      title: "Get started with Granted",
      source: CASES_LIST_NEW_CASE_BUTTON_SOURCE
    });
  };
  return <ScrollView h={validToken("90vh")} f={1} jc="flex-start" bg="$backgroundSecondary" contentContainerStyle={{
    minHeight: "100%"
  }} overflow="scroll" scrollEventThrottle={20} stickyHeaderIndices={[0]} showsVerticalScrollIndicator={false} onScroll={({
    nativeEvent
  }) => {
    if (isCloseToBottom(nativeEvent)) {
      void handleFetchMore();
    }
  }} data-sentry-element="ScrollView" data-sentry-component="CaseListLegacy" data-sentry-source-file="CaseList.tsx">
      <HorizontalSelectBar tabs={filterOptions} onTabPress={onTabPress} data-sentry-element="HorizontalSelectBar" data-sentry-source-file="CaseList.tsx">
        {!media.gtSm ? <LoadingButton onPress={handleNewCasePress} height="$2.5" w={110} mt="$-2" mr="$2">
            New case
          </LoadingButton> : null}
      </HorizontalSelectBar>

      {isEmpty ? <EmptyCaseListLegacy height={200} selectedFilter={selectedFilter} loading={loading} /> : issues.map((issue, idx) => {
      return <CaseListItemLegacy key={issue.id} issue={issue} selected={issue.id === currentIssueId} borderHide={idx === 0} />;
    })}
      <LoadMoreIndicator fetchingMore={fetchingMore} endReached={endReached} noMoreMessage="No more cases" data-sentry-element="LoadMoreIndicator" data-sentry-source-file="CaseList.tsx" />
    </ScrollView>;
};

/** Flag ON: new filter popover + CaseListInner */
const CaseListV2 = ({
  issues,
  loading,
  fetchMore,
  showRead,
  showUnread,
  showClosed,
  onFilterChange,
  currentIssueId,
  endReached,
  markKeepOpen,
  source
}: CaseListV2Props) => {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const handleNewCasePress = () => {
    pushGetHelp({
      caseType: "OTHER",
      title: "Get started with Granted",
      source: CASES_PANEL_NEW_CASE_BUTTON_SOURCE
    });
  };
  return <YStack f={1} width="100%" data-sentry-element="YStack" data-sentry-component="CaseListV2" data-sentry-source-file="CaseList.tsx">
      <XStack width="100%" backgroundColor="$background" paddingHorizontal="$sp2" borderBottomWidth={1} borderBottomColor="$uiStroke" justifyContent="space-between" alignItems="center" height="$4.5" data-sentry-element="XStack" data-sentry-source-file="CaseList.tsx">
        <Title5 data-sentry-element="Title5" data-sentry-source-file="CaseList.tsx">Cases</Title5>

        <XStack gap="$2.5" data-sentry-element="XStack" data-sentry-source-file="CaseList.tsx">
          <CaseListFilterPopover showRead={showRead} showUnread={showUnread} showClosed={showClosed} onFilterChange={onFilterChange} data-sentry-element="CaseListFilterPopover" data-sentry-source-file="CaseList.tsx" />

          <Button onPress={handleNewCasePress} size="small" fillWidth={false} variant="tertiary" icon={PlusIcon} data-sentry-element="Button" data-sentry-source-file="CaseList.tsx" />
        </XStack>
      </XStack>

      <CaseListInner issues={issues} fetchMore={fetchMore} loading={loading} showRead={showRead} showUnread={showUnread} showClosed={showClosed} endReached={endReached} currentIssueId={currentIssueId} markKeepOpen={markKeepOpen} source={source} listProps={{
      style: {
        flex: 1
      }
    }} data-sentry-element="CaseListInner" data-sentry-source-file="CaseList.tsx" />
    </YStack>;
};